import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { ViewDetailsModalComponent } from '../../../maintenance/work-order/components/modals/view-details-modal/view-details-modal.component';
import { AssetOverviewService } from '../../services/asset-overview.service';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { AuthService } from '../../../../_shared/services/auth.service';
import { CurrencyHelper } from '../../../../_shared/helpers/currency/currency-map.helper';
import { EmptyStateComponent } from '../../../../_shared/components/states/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../../_shared/components/states/error-state/error-state.component';
import { SearchComponent } from '../../../../_shared/components/filters/search/search.component';
import { DateFilterComponent } from '../../../../_shared/components/filters/date-filter/date-filter.component';
import { DateIntervalFilterData } from '../../../../_shared/models/filters/widget-filter-data.model';
import { DateInterval } from '../../../../_shared/enums/date-interval.enum';
import { MaintenanceTimelineEntryResponse, MaintenanceTimelineRow } from '../../models/asset-details-response.model';
import { MaintenanceTimelineRowComponent } from './maintenance-timeline-row/maintenance-timeline-row.component';
import { ServiceTypes } from '../../../_shared/enums/service-type.enum';
import { MaintenanceModel } from '../../../_shared/enums/maintenance-model.enum';
import { WorkOrderStatus } from '../../../maintenance/work-order/enums/work-order.enum';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-maintenance',
  standalone: true,
  imports: [CommonModule, InfiniteScrollModule, EmptyStateComponent, ErrorStateComponent, SearchComponent, DateFilterComponent, MaintenanceTimelineRowComponent, TranslocoDirective],
  templateUrl: './maintenance.component.html',
  styleUrl: './maintenance.component.scss'
})
export class MaintenanceComponent implements OnInit, OnDestroy {
  @Input() assetId: number;

  isLoading: boolean = true;
  isLoadingMore: boolean = false;
  hasError: boolean = false;
  currentPage: number = 1;
  pageSize: number = 20;
  totalPages: number = 0;
  timelineRows: MaintenanceTimelineRow[] = [];
  searchTerm: string = '';
  dateFrom: Date | null = null;
  dateTo: Date | null = null;
  dateFilter: DateIntervalFilterData = {} as DateIntervalFilterData;

  DateInterval = DateInterval;

  private assetOverviewService = inject(AssetOverviewService);
  private errorHandlerService = inject(ErrorHandlerService);
  private authService = inject(AuthService);
  private modalService = inject(NgbModal);
  private readonly transloco: TranslocoService = inject(TranslocoService);

  private currencySymbol: string = '';

  private readonly serviceTypeLabels: Record<number, string> = {
    [ServiceTypes.Minor]: this.transloco.translate('assetOverview.maintenance.serviceTypes.minor'),
    [ServiceTypes.Intermediate]: this.transloco.translate('assetOverview.maintenance.serviceTypes.intermediate'),
    [ServiceTypes.Major]: this.transloco.translate('assetOverview.maintenance.serviceTypes.major')
  };

  private readonly intervalTypeLabels: Record<string, string> = {
    Distance: 'km',
    Time: this.transloco.translate('assetOverview.maintenance.intervalTypes.months')
  };

  private readonly cancelSubject = new Subject<void>();

  ngOnInit(): void {
    const currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(currencyCode);
    void this.getMaintenanceTimelineAsync();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  onScrollDown(): void {
    if (this.isLoading || this.isLoadingMore || this.currentPage >= this.totalPages) {
      return;
    }

    this.currentPage++;
    void this.getMaintenanceTimelineAsync();
  }

  onSearchTermUpdated(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    void this.getMaintenanceTimelineAsync();
  }

  onDateFilterChanged(filter: DateIntervalFilterData): void {
    this.dateFrom = filter.startDate;
    this.dateTo = filter.endDate;
    this.currentPage = 1;
    void this.getMaintenanceTimelineAsync();
  }

  onViewDetails(row: MaintenanceTimelineRow): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      windowClass: 'view-details-modal'
    };

    const modalRef = this.modalService.open(ViewDetailsModalComponent, modalOptions);
    modalRef.componentInstance.workOrderId = row.workOrderId;

    modalRef.result.then(
      result => {
        if (result) {
          this.currentPage = 1;
          void this.getMaintenanceTimelineAsync();
        }
      },
      () => { /* dismissed - no action */ });
  }

  private async getMaintenanceTimelineAsync(): Promise<void> {
    const requestedPage = this.currentPage;
    this.hasError = false;

    try {
      if (requestedPage === 1) {
        this.isLoading = true;
        this.cancelSubject.next();
      } else {
        this.isLoadingMore = true;
      }

      const response = await this.assetOverviewService.getMaintenanceTimeline(
        {
          assetId: this.assetId,
          page: requestedPage,
          pageSize: this.pageSize,
          searchTerm: this.searchTerm.trim() || undefined,
          dateFrom: this.dateFrom ?? undefined,
          dateTo: this.dateTo ?? undefined,
          completedOnly: this.dateFrom !== null || undefined
        },
        this.cancelSubject);
      const rows = (response.data?.results ?? []).map(entry => this.mapTimelineRow(entry));

      this.timelineRows = requestedPage === 1 ? rows : [...this.timelineRows, ...rows];
      this.totalPages = response.data?.totalPages ?? 0;
      this.isLoading = false;
      this.isLoadingMore = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          if (requestedPage > 1) {
            this.currentPage--;
          }

          this.hasError = true;
          this.isLoading = false;
          this.isLoadingMore = false;
        }
      });
    }
  }

  private mapTimelineRow(entry: MaintenanceTimelineEntryResponse): MaintenanceTimelineRow {
    const isCompleted = entry.status === WorkOrderStatus.Completed;
    const { statusLabel, statusDate, showDuePrefix } = this.resolveStatusDisplay(entry);
    const { costLabel, costTooltip } = this.resolveCostDisplay(entry, isCompleted);

    return {
      workOrderId: entry.workOrderId,
      workOrderNumber: entry.workOrderNumber,
      serviceInterval: entry.intervalValue && entry.intervalType
        ? `${entry.intervalValue.toLocaleString('default')} ${this.intervalTypeLabels[entry.intervalType] ?? entry.intervalType}`
        : '-',
      serviceTypeLabel: entry.serviceType !== null ? this.serviceTypeLabels[entry.serviceType] ?? '-' : '-',
      partsUsed: entry.partsUsed ?? [],
      costLabel,
      costTooltip,
      workshop: entry.workshop || '-',
      technician: entry.technician || '-',
      downtimeLabel: entry.downtimeHours !== null
        ? this.transloco.translate('assetOverview.maintenance.hours', { hours: entry.downtimeHours.toLocaleString('default', { maximumFractionDigits: 1 }) })
        : '-',
      isCompleted,
      statusLabel,
      statusDate,
      showDuePrefix,
      isInHouse: entry.maintenanceModel !== MaintenanceModel.Contract
    };
  }

  private resolveCostDisplay(entry: MaintenanceTimelineEntryResponse, isCompleted: boolean): Pick<MaintenanceTimelineRow, 'costLabel' | 'costTooltip'> {
    const symbol = entry.currencyCode ? CurrencyHelper.getCurrencySymbol(entry.currencyCode) : this.currencySymbol;
    const showActual = isCompleted && entry.actualCost !== null;
    const amount = showActual ? entry.actualCost : entry.cost;

    if (amount === null) {
      return { costLabel: '-', costTooltip: null };
    }

    const costLabel = `${symbol}${amount.toLocaleString('default', { maximumFractionDigits: 2 })}`;

    if (!showActual) {
      return { costLabel, costTooltip: null };
    }

    const rateSymbol = entry.labourRateCurrencyCode ? CurrencyHelper.getCurrencySymbol(entry.labourRateCurrencyCode) : symbol;
    const costTooltip = entry.downtimeHours !== null && entry.hourlyLabourRate !== null
      ? this.transloco.translate('assetOverview.maintenance.costTooltip.breakdown', {
        hours: entry.downtimeHours.toLocaleString('default', { maximumFractionDigits: 1 }),
        rate: `${rateSymbol}${entry.hourlyLabourRate.toLocaleString('default', { maximumFractionDigits: 2 })}`,
        total: costLabel
      })
      : this.transloco.translate('assetOverview.maintenance.costTooltip.actual');

    return { costLabel, costTooltip };
  }

  private resolveStatusDisplay(entry: MaintenanceTimelineEntryResponse): Pick<MaintenanceTimelineRow, 'statusLabel' | 'statusDate' | 'showDuePrefix'> {
    if (entry.status === WorkOrderStatus.Completed) {
      return {
        statusLabel: this.transloco.translate('assetOverview.maintenance.status.completed'),
        statusDate: entry.completedAt ? new Date(entry.completedAt) : null,
        showDuePrefix: false
      };
    }

    if (entry.status === WorkOrderStatus.InProgress) {
      return {
        statusLabel: this.transloco.translate('assetOverview.maintenance.status.inProgress'),
        statusDate: entry.dueDate ? new Date(entry.dueDate) : null,
        showDuePrefix: true
      };
    }

    if (entry.status === WorkOrderStatus.Open && entry.rescheduledAt !== null && entry.scheduledDate !== null) {
      return {
        statusLabel: this.transloco.translate('assetOverview.maintenance.status.rescheduled'),
        statusDate: new Date(entry.scheduledDate),
        showDuePrefix: false
      };
    }

    return {
      statusLabel: this.transloco.translate('assetOverview.maintenance.status.nextServiceDue'),
      statusDate: entry.dueDate ? new Date(entry.dueDate) : null,
      showDuePrefix: false
    };
  }

}
